import { useEffect, useMemo, useState } from 'react'
import type { Product } from 'src/components/restructure/product/types/product'
import { clientStore } from 'src/services/axiosInstance'

import type { FeaturedProduct } from './types'

type UseFeaturedProductsDataParams = {
  productIds: string[]
}

type UseFeaturedProductsDataResult = {
  loading: boolean
  products: FeaturedProduct[]
}

function toResolvedProduct(product: Product): FeaturedProduct | null {
  const firstItem = product?.items?.[0]
  const firstImage = firstItem?.images?.[0]?.imageUrl
  const isKit = firstItem?.isKit
  const productUrl = product?.linkText
    ? `/${product.linkText}/${isKit ? 'kit' : 'p'}`
    : product?.link

  const hasAvailableSeller = product?.items?.some((item) =>
    item?.sellers?.some((seller) => {
      const offer = seller?.commertialOffer

      return Boolean(offer?.AvailableQuantity && offer.AvailableQuantity > 0)
    })
  )

  if (
    !product?.productId ||
    !product?.productName ||
    !firstImage ||
    !productUrl
  ) {
    return null
  }

  if (!hasAvailableSeller) {
    return null
  }

  return {
    productId: product.productId,
    productName: product.productName,
    imageUrl: firstImage,
    productUrl,
  }
}

export function useFeaturedProductsData({
  productIds,
}: UseFeaturedProductsDataParams): UseFeaturedProductsDataResult {
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState<FeaturedProduct[]>([])

  const dedupedProductIds = useMemo(
    () => Array.from(new Set(productIds.filter(Boolean))),
    [productIds]
  )

  useEffect(() => {
    let active = true

    async function loadProducts() {
      if (dedupedProductIds.length === 0) {
        if (active) {
          setProducts([])
          setLoading(false)
        }

        return
      }

      setLoading(true)

      try {
        const query = dedupedProductIds
          .map((id) => `productId=${encodeURIComponent(id)}`)
          .join('&')

        const { data } = await clientStore.get<{ products: Product[] }>(
          `/v1/api/decathlon-pdp/products?${query}`
        )

        if (!active) {
          return
        }

        const resolvedProducts = (data?.products ?? [])
          .map(toResolvedProduct)
          .filter((item): item is FeaturedProduct => item !== null)

        setProducts(resolvedProducts)
      } catch (error) {
        if (active) {
          console.error(
            'FeaturedProducts: erro ao resolver produtos por ID',
            error
          )
          setProducts([])
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    loadProducts()

    return () => {
      active = false
    }
  }, [dedupedProductIds])

  return {
    loading,
    products,
  }
}
